import { create } from 'zustand'

export type ThemePreference = 'light' | 'dark' | 'system'
const KEY = 'facture-theme'

function read(): ThemePreference {
  try {
    const value = localStorage.getItem(KEY)
    if (value === 'light' || value === 'dark' || value === 'system') return value
    return 'light'
  } catch {
    return 'light'
  }
}

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null

function apply(preference: ThemePreference) {
  const dark = preference === 'dark' || (preference === 'system' && !!media?.matches)
  const root = document.documentElement
  // Evita que todas las transiciones se disparen al cambiar de tema.
  root.classList.add('[&_*]:transition-none')
  root.classList.toggle('dark', dark)
  requestAnimationFrame(() => root.classList.remove('[&_*]:transition-none'))
  return dark
}

/** Punto de la pantalla desde donde se expande el cambio de tema (donde se hizo clic). */
export interface ThemeOrigin {
  x: number
  y: number
}

interface ThemeState {
  preference: ThemePreference
  resolved: 'light' | 'dark'
  setPreference: (preference: ThemePreference, origin?: ThemeOrigin) => void
}

/** Cambia la clase del tema; con View Transitions el tema nuevo se revela en círculo desde `origin`. */
function transition(change: () => void, origin?: ThemeOrigin) {
  const root = document.documentElement
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const start = (
    document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } }
  ).startViewTransition
  if (!start || reduced || !origin) {
    change()
    return
  }
  const x = origin.x
  const y = origin.y
  const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y))
  const run = start.call(document, change)
  run.ready
    .then(() => {
      root.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        {
          duration: 650,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          pseudoElement: '::view-transition-new(root)',
        },
      )
    })
    .catch(() => undefined)
}

export const useTheme = create<ThemeState>((set) => {
  const preference = read()
  media?.addEventListener('change', () => {
    const current = useTheme.getState().preference
    if (current === 'system') set({ resolved: apply('system') ? 'dark' : 'light' })
  })
  return {
    preference,
    resolved:
      typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
        ? 'dark'
        : 'light',
    setPreference: (next, origin) => {
      try {
        localStorage.setItem(KEY, next)
      } catch {
        /* sin almacenamiento */
      }
      transition(() => set({ preference: next, resolved: apply(next) ? 'dark' : 'light' }), origin)
    },
  }
})
